import { useSiteContent } from "@/lib/siteContent";
import { useI18n } from "@/lib/i18n";
import SEOHead from "@/components/SEOHead";
import { dentistJsonLd, breadcrumbJsonLd } from "@/lib/schema";

// Equipo profesional — orden solicitado por la clínica.
// Cada entrada trae imagen local (public/team/) + alt SEO descriptivo con ubicación.
const TEAM = [
  {
    id: "katia-falon",
    image: "/team/katia-falon.webp",
    nameKey: "about.team1_name",
    roleKey: "about.team1_role",
    alt:
      "Dra. Katia Jimena Falón Uribe, odontóloga y directora clínica de Katia Falón Odontología, ajustando la lámpara del consultorio dental en Armenia, Quindío",
  },
  {
    id: "janeth-correa",
    image: "/team/janeth-correa.jpeg",
    nameKey: "about.team2_name",
    roleKey: "about.team2_role",
    alt:
      "Dra. Janeth Correa, ortodoncista y ortopedista maxilar del equipo de Katia Falón Odontología en Armenia, Quindío",
  },
  {
    id: "daniel-moscoso",
    image: "/team/daniel-moscoso.webp",
    nameKey: "about.team3_name",
    roleKey: "about.team3_role",
    alt:
      "Dr. Daniel Moscoso, odontólogo del equipo profesional de Katia Falón Odontología en Armenia, Quindío",
  },
];

export default function About() {
  const c = useSiteContent("about_hero");
  const { t, pick } = useI18n();

  return (
    <section data-testid="about-page" className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
      <SEOHead
        title="Sobre nosotros · Odontología en Armenia, Quindío · Katia Falón Odontología"
        description="Conoce el equipo de Katia Falón Odontología: odontología sin dolor y odontopediatría en Armenia, Quindío. Cra. 14 #17N-32. Atención personalizada para adultos y niños."
        path="/sobre-nosotros"
        image="/logo-katia.png"
        jsonLd={[
          dentistJsonLd(),
          breadcrumbJsonLd([
            { name: "Inicio", path: "/" },
            { name: "Sobre nosotros", path: "/sobre-nosotros" },
          ]),
        ]}
      />
      <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--clinic-muted))]">
            {pick(c.eyebrow, "about.eyebrow")}
          </div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl">
            {pick(c.title_start, "about.title_start")}
            <em className="italic text-[hsl(var(--clinic-secondary))]">
              {pick(c.title_highlight, "about.title_highlight")}
            </em>
            {pick(c.title_end, "about.title_end")}
          </h1>
          <p className="mt-6 text-base leading-relaxed text-[hsl(var(--clinic-muted))]">
            {pick(c.body, "about.body")}
          </p>
          <div className="mt-10 space-y-6">
            <Block title={pick(c.block1_title, "about.block1_title")} body={pick(c.block1_body, "about.block1_body")} />
            <Block title={pick(c.block2_title, "about.block2_title")} body={pick(c.block2_body, "about.block2_body")} />
            <Block title={pick(c.block3_title, "about.block3_title")} body={pick(c.block3_body, "about.block3_body")} />
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--clinic-muted))]">
            {t("about.teamEyebrow")}
          </div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl">
            {t("about.teamTitle")}
          </h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-1" data-testid="team-list">
            {TEAM.map((member) => (
              <figure
                key={member.id}
                data-testid={`team-card-${member.id}`}
                className="overflow-hidden rounded-2xl border border-[hsl(var(--clinic-border))] bg-white"
              >
                <img
                  src={member.image}
                  alt={member.alt}
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/5] w-full object-cover"
                  data-testid={`team-image-${member.id}`}
                />
                <figcaption className="p-4">
                  <div className="font-display text-lg">{t(member.nameKey)}</div>
                  <div className="mt-1 text-xs text-[hsl(var(--clinic-muted))]">{t(member.roleKey)}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Block({ title, body, pending }) {
  return (
    <div className="rounded-2xl border border-[hsl(var(--clinic-border))] bg-white p-6">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-2xl">{title}</h3>
        {pending && (
          <span className="rounded-full bg-[hsl(var(--clinic-bg))] px-3 py-1 text-[10px] uppercase tracking-widest text-[hsl(var(--clinic-muted))]">
            pendiente de validación
          </span>
        )}
      </div>
      <p className="mt-3 text-sm leading-relaxed text-[hsl(var(--clinic-muted))]">{body}</p>
    </div>
  );
}
